import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, operationsClient } from '@/lib/api/client';
import { customFieldEndpoints } from '@/lib/api/endpoints';
import { customFieldKeys } from '@/lib/queryKeys';
import type { CustomFieldDefinition, DeleteDefinitionResult } from '@/types/custom-fields';

interface DeleteDefinitionContext {
  previous?: CustomFieldDefinition[];
}

/**
 * Delete a custom field definition.
 *
 * Backend semantics:
 *  - no recorded values → hard delete (row + dependent associations gone)
 *  - any recorded values → soft delete (isActive=false on the definition)
 *
 * In both cases we optimistically remove the row from the cached definitions
 * list so the settings page reflects the new state immediately. The trailing
 * invalidate keeps soft-deleted defs around for the "Inactive" filter to find.
 */
export function useDeleteDefinition() {
  const queryClient = useQueryClient();
  return useApiMutation<DeleteDefinitionResult, string, DeleteDefinitionContext>({
    mutationFn: (id) =>
      apiDelete<DeleteDefinitionResult>(operationsClient, customFieldEndpoints.definition(id)),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: customFieldKeys.definitions() });
      const previous = queryClient.getQueryData<CustomFieldDefinition[]>(
        customFieldKeys.definitions(),
      );
      if (previous) {
        queryClient.setQueryData<CustomFieldDefinition[]>(
          customFieldKeys.definitions(),
          previous.filter((definition) => definition.id !== id),
        );
      }
      return { previous };
    },
    onError: (_error, _id, onMutateResult) => {
      if (onMutateResult?.previous) {
        queryClient.setQueryData(customFieldKeys.definitions(), onMutateResult.previous);
      }
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
      if (result?.mode === 'soft') {
        toast.success('Custom field disabled — existing values retained');
      } else {
        toast.success('Custom field deleted');
      }
    },
  });
}
